import { useEffect, useState } from 'react';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import { BarChart3, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  getClaudeMetricsSummary,
  type ClaudeMetricsSummary,
} from '@shared/services/admin-api';

// The ≥80% cache-hit rate is the Phase-1 launch gate (Recal Dev Plan §2.3).
const CACHE_HIT_GATE = 0.8;

const WINDOWS = [
  { label: '24h', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
];

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function usd(n: number): string {
  return n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(4)}`;
}

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${n}`;
}

export function AdminAnalytics() {
  const [windowDays, setWindowDays] = useState(7);
  const [data, setData] = useState<ClaudeMetricsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getClaudeMetricsSummary(windowDays)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load Claude metrics.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  const gateMet = data?.overall ? data.overall.cacheHitRate >= CACHE_HIT_GATE : false;
  const maxDayHit = data?.byDay?.length
    ? Math.max(0.0001, ...data.byDay.map((d) => d.cacheHitRate))
    : 1;

  return (
    <div className="min-h-screen bg-dark-bg">
      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-2xl font-bold mb-1">Analytics</h1>
              <p className="text-content-muted text-sm">
                Claude call metrics, cost tracking, and the cache-hit launch gate
              </p>
            </div>
            <div className="flex items-center gap-1 rounded-lg bg-dark-surface border border-dark-border p-1">
              {WINDOWS.map((w) => (
                <button
                  key={w.days}
                  onClick={() => setWindowDays(w.days)}
                  className={`px-3 py-1.5 text-xs rounded-md font-body transition-colors ${
                    windowDays === w.days
                      ? 'bg-indigo-500 text-white'
                      : 'text-content-muted hover:text-content'
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          {loading && (
            <div className="rounded-2xl bg-dark-surface border border-dark-border p-10 flex items-center justify-center text-content-muted">
              <RefreshCw size={18} className="animate-spin mr-2" /> Loading metrics…
            </div>
          )}

          {error && !loading && (
            <div className="rounded-2xl bg-dark-surface border border-error/40 p-10 flex flex-col items-center text-center">
              <AlertTriangle size={32} className="text-error mb-3" />
              <p className="text-sm text-content-muted">{error}</p>
            </div>
          )}

          {data?.overall && !loading && !error && (
            <div className="space-y-5">
              {data.overall.calls === 0 && (
                <div className="rounded-xl bg-dark-surface border border-dark-border p-6 flex items-center gap-3 text-content-muted">
                  <BarChart3 size={20} />
                  <span className="text-sm">No Claude calls recorded in this window yet.</span>
                </div>
              )}

              {/* Cache-hit gate hero */}
              <div
                className={`rounded-2xl border p-6 ${
                  gateMet ? 'border-success/50 bg-success/5' : 'border-gold-500/40 bg-gold-500/5'
                }`}
              >
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-content-muted mb-1">
                      Cache-hit rate (launch gate ≥ {pct(CACHE_HIT_GATE)})
                    </p>
                    <p
                      className={`font-heading text-4xl font-bold ${
                        gateMet ? 'text-success' : 'text-gold-400'
                      }`}
                    >
                      {pct(data.overall.cacheHitRate)}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      gateMet ? 'bg-success/20 text-success' : 'bg-gold-500/20 text-gold-300'
                    }`}
                  >
                    {gateMet ? 'Gate met' : 'Below gate'}
                  </span>
                </div>
                {/* Progress to gate */}
                <div className="mt-4 h-2 rounded-full bg-dark-border overflow-hidden">
                  <div
                    className={gateMet ? 'h-full bg-success' : 'h-full bg-gold-500'}
                    style={{ width: pct(Math.min(1, data.overall.cacheHitRate)) }}
                  />
                </div>
              </div>

              {/* Summary stat cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total calls" value={compact(data.overall.calls)} />
                <StatCard label="Claude cost" value={usd(data.overall.costUsd)} />
                <StatCard label="Output tokens" value={compact(data.overall.outputTokens)} />
                <StatCard
                  label="Verbatim flags"
                  value={`${data.overall.verbatimHits}`}
                  tone={data.overall.verbatimHits > 0 ? 'warn' : 'default'}
                />
              </div>

              {/* Daily cache-hit trend */}
              {data.byDay.length > 0 && (
                <div className="rounded-2xl bg-dark-surface border border-dark-border p-5">
                  <h2 className="font-heading text-sm font-semibold mb-4">Cache-hit rate by day</h2>
                  <div className="flex items-end gap-2 h-32">
                    {data.byDay.map((d) => (
                      <div key={d.key} className="flex-1 flex flex-col items-center justify-end gap-1">
                        <span className="text-[10px] text-content-dim">{pct(d.cacheHitRate)}</span>
                        <div
                          className={`w-full rounded-t ${
                            d.cacheHitRate >= CACHE_HIT_GATE ? 'bg-success' : 'bg-gold-500'
                          }`}
                          style={{ height: `${(d.cacheHitRate / maxDayHit) * 100}%`, minHeight: 2 }}
                          title={`${d.key}: ${pct(d.cacheHitRate)} (${d.calls} calls)`}
                        />
                        <span className="text-[10px] text-content-dim">{d.key.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By telemetry tag */}
              {data.byTag.length > 0 && (
                <div className="rounded-2xl bg-dark-surface border border-dark-border p-5 overflow-x-auto">
                  <h2 className="font-heading text-sm font-semibold mb-4">By telemetry tag</h2>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-content-muted text-xs border-b border-dark-border">
                        <th className="text-left font-medium py-2 pr-4">Tag</th>
                        <th className="text-right font-medium py-2 px-4">Calls</th>
                        <th className="text-right font-medium py-2 px-4">Cache hit</th>
                        <th className="text-right font-medium py-2 pl-4">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byTag.map((t) => (
                        <tr key={t.key} className="border-b border-dark-border/50 last:border-0">
                          <td className="py-2 pr-4 font-mono text-xs text-content">{t.key}</td>
                          <td className="py-2 px-4 text-right text-content-muted">{compact(t.calls)}</td>
                          <td
                            className={`py-2 px-4 text-right font-medium ${
                              t.cacheHitRate >= CACHE_HIT_GATE ? 'text-success' : 'text-gold-400'
                            }`}
                          >
                            {pct(t.cacheHitRate)}
                          </td>
                          <td className="py-2 pl-4 text-right text-content-muted">{usd(t.costUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {data.truncated && (
                <p className="text-xs text-gold-400">
                  Showing a capped sample — results may be partial for this window.
                </p>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="rounded-xl bg-dark-surface border border-dark-border p-4">
      <p className="text-xs text-content-muted mb-1">{label}</p>
      <p
        className={`font-heading text-2xl font-bold ${
          tone === 'warn' ? 'text-gold-400' : 'text-content'
        }`}
      >
        {value}
      </p>
    </div>
  );
}
